import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { cn } from "@/lib/utils";

const NAV_LINKS = [
  { label: "Leistungen", href: "/#leistungen" },
  { label: "Konzept & Vorgehen", href: "/#konzept" },
  { label: "Über Uns", href: "/#ueber-uns" },
  { label: "Kontakt", href: "/#kontakt" },
];

export const Logo = ({ className, imageClassName }) => (
  <Link to="/" aria-label="WANGO MEDIA - Zur Startseite" className={cn("flex items-center gap-2.5 shrink-0", className)}>
    <img
      src="/logo-dark.svg"
      alt="WANGO MEDIA Logo"
      width="313"
      height="68"
      fetchPriority="high"
      className={cn("h-14 sm:h-16 md:h-18 w-auto object-contain max-h-[68px] transition-transform duration-200 hover:scale-[1.02]", imageClassName)}
    />
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let prevScrolled = false;
    const onScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== prevScrolled) {
        prevScrolled = isScrolled;
        setScrolled(isScrolled);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-all duration-300",
        scrolled ? "glass-strong py-1" : "bg-transparent py-2"
      )}
    >
      <div className="mx-auto flex h-[84px] max-w-7xl items-center justify-between px-4 sm:px-8">
        <Logo />

        <nav aria-label="Hauptnavigation" className="hidden items-center gap-8 lg:flex">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.href.replace("/#", "")}`}
              className="text-sm font-semibold tracking-wide text-muted-foreground transition-colors duration-200 hover:text-[#C5A059]"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Button asChild data-testid="nav-cta" className="rounded-full bg-[#C5A059] px-7 py-5 font-semibold text-[#0A0A0A] hover:bg-[#b08e4c] transition-all duration-200 shadow-md hover:shadow-[#C5A059]/20">
            <a href="/#kontakt">Kostenloses Erstgespräch</a>
          </Button>
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <Button
              variant="outline"
              size="icon"
              aria-label="Menü öffnen"
              data-testid="mobile-menu-trigger"
              className="h-12 w-12 rounded-xl border-white/20 bg-[#18181B] text-white shadow-lg hover:bg-[#27272A] hover:border-[#C5A059] active:scale-95 transition-all shrink-0"
            >
              <Menu className="h-6 w-6 text-white" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[320px] border-white/10 bg-[#121212] p-6 flex flex-col justify-between">
            <div>
              <SheetTitle className="sr-only">Navigationsmenü</SheetTitle>
              <div className="mt-2 pb-6 border-b border-white/10 flex items-center justify-between">
                <Logo onClick={() => setOpen(false)} imageClassName="h-14 w-auto" />
              </div>
              <nav aria-label="Mobile Navigation" className="mt-8 flex flex-col gap-2">
                {NAV_LINKS.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${link.href.replace("/#", "")}`}
                    className="rounded-xl px-4 py-3.5 text-lg font-medium text-muted-foreground transition-all duration-200 hover:bg-white/5 hover:text-[#C5A059] active:bg-[#C5A059]/10"
                  >
                    {link.label}
                  </a>
                ))}
              </nav>
            </div>
            <div className="pb-4">
              <Button asChild className="w-full rounded-full bg-[#C5A059] py-6 text-base font-semibold text-[#0A0A0A] hover:bg-[#b08e4c] shadow-lg shadow-[#C5A059]/15" data-testid="mobile-nav-cta">
                <a href="/#kontakt" onClick={() => setOpen(false)}>Kostenloses Erstgespräch</a>
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
